import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete, Box, Button, CircularProgress, MenuItem, Paper, Select, Tab, Tabs, TextField, Typography, Divider } from "@mui/material";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faSyncAlt,
  faUser,
  faDumbbell,
  faFileExcel,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import TrainerMemberLogTable from '../../personalTrainers/TrainerMemberLogTable';
import {
    EXCEL_DATE_FORMAT,
    finalizeWorksheetDates,
    toExcelSerialDate,
} from '../../../../utils/reportExcelExport';
import { isDynamicStaffRole } from '../../../../utils/roleAccess';

const BASE_URL = process.env.REACT_APP_BASE_URL;

function MemberTrainerLogReport() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [tab, setTab] = useState(tabParam === 'trainer' ? 1 : 0);

  const [role, setRole] = useState('');
  const [company_id, setCompanyID] = useState('');
  const [location_id, setLocationID] = useState('');

  const [memberInputValue, setMemberInputValue] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberOptionsLoading, setMemberOptionsLoading] = useState(false);
  const [memberOptionsPage, setMemberOptionsPage] = useState(1);
  const [memberOptionsHasMore, setMemberOptionsHasMore] = useState(false);
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberLogs, setMemberLogs] = useState([]);
  const [memberLogsLoading, setMemberLogsLoading] = useState(false);

  const [trainers, setTrainers] = useState([]);
  const [selectedTrainerId, setSelectedTrainerId] = useState('');
  const [trainerLogs, setTrainerLogs] = useState([]);
  const [trainerLogsLoading, setTrainerLogsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const memberSearchDebounceRef = useRef(null);
  const memberSearchAbortRef = useRef(null);
  const memberSearchQueryRef = useRef('');

  useEffect(() => {
    const sessionStr = sessionStorage.getItem('user-info');
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      setRole(session.user.role);
      setCompanyID(session.user.company_id);
      setLocationID(session.user.location_id);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (!role) return;
    const loadTrainers = async () => {
      try {
        let url = '';
        if (role === 'SuperAdmin') url = `${BASE_URL}trainers/get-trainers`;
        else if (role === 'Admin') url = `${BASE_URL}trainers/get-trainers-by-company/${company_id}`;
        else if (role === 'ClubAdmin') url = `${BASE_URL}trainers/get-trainers-by-location/${location_id}`;
        else if (isDynamicStaffRole(role)) url = `${BASE_URL}trainers/get-trainers-by-location/${location_id}`;
        if (!url) return;
        const res = await fetch(url);
        const data = await res.json();
        if (data.trainers) setTrainers(data.trainers);
      } catch (e) {
        console.error(e);
      }
    };
    loadTrainers();
  }, [role, company_id, location_id]);

  const buildMemberDropdownParams = useCallback(
    (query, page) => {
      const params = new URLSearchParams({
        search: query.trim(),
        page: String(page),
        limit: '20',
      });
      if (role === 'Admin' && company_id) {
        params.set('company_id', company_id);
      } else if (role === 'ClubAdmin' && location_id) {
        params.set('location_id', location_id);
      } else if (isDynamicStaffRole(role) && location_id) {
        params.set('location_id', location_id);
      } else {
        if (company_id) params.set('company_id', company_id);
        if (location_id) params.set('location_id', location_id);
      }
      return params;
    },
    [role, company_id, location_id]
  );

  const fetchMemberDropdown = useCallback(
    async (query, page = 1, append = false) => {
      if (!role || !query.trim()) {
        setMemberOptions([]);
        setMemberOptionsPage(1);
        setMemberOptionsHasMore(false);
        return;
      }

      if (memberSearchAbortRef.current) {
        memberSearchAbortRef.current.abort();
      }
      const controller = new AbortController();
      memberSearchAbortRef.current = controller;
      memberSearchQueryRef.current = query.trim();

      setMemberOptionsLoading(true);
      try {
        const params = buildMemberDropdownParams(query, page);
        const res = await fetch(`${BASE_URL}members/search-dropdown?${params}`, {
          signal: controller.signal,
        });
        const data = await res.json();
        if (memberSearchQueryRef.current !== query.trim()) return;

        const members = data.members || [];
        const pagination = data.pagination || {};
        setMemberOptions((prev) => (append ? [...prev, ...members] : members));
        setMemberOptionsPage(page);
        setMemberOptionsHasMore(Boolean(pagination.hasMore));
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error(e);
          if (!append) setMemberOptions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setMemberOptionsLoading(false);
        }
      }
    },
    [role, buildMemberDropdownParams]
  );

  const scheduleMemberDropdownSearch = useCallback(
    (query) => {
      if (memberSearchDebounceRef.current) {
        clearTimeout(memberSearchDebounceRef.current);
      }
      memberSearchDebounceRef.current = setTimeout(() => {
        fetchMemberDropdown(query, 1, false);
      }, 350);
    },
    [fetchMemberDropdown]
  );

  const loadMoreMemberOptions = useCallback(() => {
    if (!memberOptionsHasMore || memberOptionsLoading || !memberInputValue.trim()) {
      return;
    }
    fetchMemberDropdown(memberInputValue, memberOptionsPage + 1, true);
  }, [
    memberOptionsHasMore,
    memberOptionsLoading,
    memberInputValue,
    memberOptionsPage,
    fetchMemberDropdown,
  ]);

  const getMemberOptionLabel = (option) => {
    if (!option) return '';
    const status = option.is_active === false ? ' · Inactive' : '';
    return `${option.name ?? ''} (${option.member_id ?? '—'})${status}`;
  };

  const loadMemberLogs = async (memberId) => {
    setMemberLogsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}members/get-member-trainer-logs/${memberId}`);
      const data = await res.json();
      if (res.ok) {
        setSelectedMember(data.member);
        setAutocompleteValue(data.member);
        setMemberInputValue(
          data.member?.name
            ? `${data.member.name} (${data.member.member_id ?? ''})`
            : ''
        );
        setMemberLogs(data.logs || []);
      } else {
        setSelectedMember(null);
        setAutocompleteValue(null);
        setMemberLogs([]);
      }
    } catch (e) {
      console.error(e);
      setMemberLogs([]);
    } finally {
      setMemberLogsLoading(false);
    }
  };

  const loadTrainerLogs = async (trainerId) => {
    if (!trainerId) {
      setTrainerLogs([]);
      return;
    }
    setTrainerLogsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}trainers/get-trainer-member-logs/${trainerId}`);
      const data = await res.json();
      if (res.ok && data.logs) setTrainerLogs(data.logs);
      else setTrainerLogs([]);
    } catch (e) {
      console.error(e);
      setTrainerLogs([]);
    } finally {
      setTrainerLogsLoading(false);
    }
  };

  useEffect(() => {
    const memberId = searchParams.get('member_id');
    const trainerId = searchParams.get('trainer_id');
    if (memberId && tab === 0) {
      loadMemberLogs(memberId);
    }
    if (trainerId && tab === 1) {
      setSelectedTrainerId(trainerId);
      loadTrainerLogs(trainerId);
    }
  }, []);

  const handleSelectMember = (m) => {
    if (!m) return;
    setSearchParams({ tab: 'member', member_id: m._id });
    loadMemberLogs(m._id);
  };

  const handleClearMemberSelection = () => {
    setSelectedMember(null);
    setAutocompleteValue(null);
    setMemberLogs([]);
    setMemberInputValue('');
    setMemberOptions([]);
    setMemberOptionsHasMore(false);
    setSearchParams({ tab: 'member' });
  };

  const handleTrainerChange = (trainerId) => {
    setSelectedTrainerId(trainerId);
    setSearchParams({ tab: 'trainer', trainer_id: trainerId });
    loadTrainerLogs(trainerId);
  };

  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    setSearchParams(newTab === 0 ? { tab: 'member' } : { tab: 'trainer' });
  };

  const handleExport = () => {
    const rows =
      tab === 0
        ? memberLogs.map((row) => ({
            'Member ID': selectedMember?.member_id,
            'Member Name': selectedMember?.name,
            Trainer: row.trainerName,
            Assigned: toExcelSerialDate(row.assignedAt) ?? '',
            Ended: row.isActive ? 'Active' : (toExcelSerialDate(row.endedAt) ?? ''),
            Months: row.trainedMonths,
            Status: row.isActive ? 'Active' : 'Historical',
          }))
        : trainerLogs.map((row) => ({
            'Member ID': row.memberCode,
            'Member Name': row.memberName,
            Phone: row.phone,
            Assigned: toExcelSerialDate(row.assignedAt) ?? '',
            Ended: row.isActive ? 'Active' : (toExcelSerialDate(row.endedAt) ?? ''),
            Months: row.trainedMonths,
            Status: row.isActive ? 'Active' : 'Historical',
          }));

    if (rows.length === 0) return;
    setExporting(true);
    try {
      const ws = XLSX.utils.json_to_sheet(rows);
      finalizeWorksheetDates(ws, {
        Assigned: EXCEL_DATE_FORMAT,
        Ended: EXCEL_DATE_FORMAT,
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Trainer Assignments');
      XLSX.writeFile(wb, `Trainer_Assignments_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  const activeLogs = tab === 0 ? memberLogs : trainerLogs;

  return (
    <>

    
      <div className="p-4 md:p-8 bg-gx-canvas min-h-screen">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <Box>
            <Typography variant="h4" className="font-extrabold text-gx-navy flex items-center gap-3">
              <div className="bg-gx-navy p-3 rounded-gx shadow-gx">
                <FontAwesomeIcon icon={faHistory} className="text-white text-2xl" />
              </div>
              Trainer Assignments
            </Typography>
            <Typography variant="body2" className="text-gx-ink-soft mt-1 ml-1">
              View coaching history by member or trainer. Each row is one assignment period.
            </Typography>
          </Box>
          <div className="flex gap-3">
            <Button
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faSyncAlt} />}
              onClick={() => {
                if (tab === 0 && selectedMember) loadMemberLogs(selectedMember._id);
                if (tab === 1 && selectedTrainerId) loadTrainerLogs(selectedTrainerId);
              }}
              className="border-gx-navy text-gx-navy capitalize font-bold"
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={
                exporting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FontAwesomeIcon icon={faFileExcel} />
                )
              }
              onClick={handleExport}
              disabled={activeLogs.length === 0 || exporting}
              className="bg-[#107c41] hover:bg-[#0e6b38] capitalize font-bold"
            >
              Export Excel
            </Button>
          </div>
        </div>

        <Paper elevation={0} className="rounded-2xl border border-gx-border bg-white shadow-gx mb-6">
          <Tabs value={tab} onChange={handleTabChange} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<FontAwesomeIcon icon={faUser} />} iconPosition="start" label="By Member" />
            <Tab icon={<FontAwesomeIcon icon={faDumbbell} />} iconPosition="start" label="By Trainer" />
          </Tabs>

          {tab === 0 && (
            <Box className="p-6">
              <Typography className="font-bold text-gx-ink mb-3">Search member</Typography>
              <Autocomplete
                className="max-w-xl mb-4"
                options={memberOptions}
                value={autocompleteValue}
                inputValue={memberInputValue}
                onInputChange={(_, newInputValue, reason) => {
                  setMemberInputValue(newInputValue);
                  if (reason === 'input') {
                    scheduleMemberDropdownSearch(newInputValue);
                  }
                  if (reason === 'clear') {
                    setMemberOptions([]);
                    setMemberOptionsHasMore(false);
                  }
                }}
                onChange={(_, newValue) => {
                  setAutocompleteValue(newValue);
                  if (newValue) {
                    handleSelectMember(newValue);
                  } else {
                    handleClearMemberSelection();
                  }
                }}
                getOptionLabel={getMemberOptionLabel}
                isOptionEqualToValue={(a, b) =>
                  Boolean(a && b && String(a._id) === String(b._id))
                }
                filterOptions={(x) => x}
                loading={memberOptionsLoading}
                noOptionsText={
                  memberInputValue.trim()
                    ? 'No members found'
                    : 'Type name, phone, or member ID to search'
                }
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box>
                      <Typography variant="body2" className="font-semibold">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" className="text-gx-ink-soft">
                        ID: {option.member_id ?? '—'} · {option.phone || '—'}
                        {option.is_active === false ? ' · Inactive' : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const node = event.currentTarget;
                    if (
                      node.scrollTop + node.clientHeight >= node.scrollHeight - 8
                    ) {
                      loadMoreMemberOptions();
                    }
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Member"
                    placeholder="Name, phone, or member ID..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {memberOptionsLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {selectedMember && (
                <>
                  <Divider className="my-4" />
                  <Paper className="p-4 mb-4 bg-gx-canvas border border-gx-border">
                    <Typography className="font-bold text-lg">{selectedMember.name}</Typography>
                    <Typography variant="body2" className="text-gx-ink-soft">
                      Member ID: {selectedMember.member_id} · Phone: {selectedMember.phone || '—'}
                    </Typography>
                    <Typography variant="body2" className="text-gx-ink-soft mt-1">
                      Current trainer:{' '}
                      {selectedMember.currentTrainerName ||
                        (selectedMember.is_trainer ? 'Assigned' : 'None')}
                    </Typography>
                    <Button size="small" className="mt-2" onClick={handleClearMemberSelection}>
                      Clear selection
                    </Button>
                  </Paper>
                  {memberLogsLoading ? (
                    <div className="flex justify-center py-12">
                      <CircularProgress color="secondary" />
                    </div>
                  ) : (
                    <TrainerMemberLogTable logs={memberLogs} mode="member" />
                  )}
                </>
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box className="p-6">
              <Typography className="font-bold text-gx-ink mb-3">Select trainer</Typography>
              <Select
                value={selectedTrainerId}
                onChange={(e) => handleTrainerChange(e.target.value)}
                displayEmpty
                className="min-w-[280px] mb-4"
                size="small"
              >
                <MenuItem value="">
                  <em>Choose a trainer</em>
                </MenuItem>
                {trainers.map((t) => (
                  <MenuItem key={t._id} value={t._id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>

              {trainerLogsLoading ? (
                <div className="flex justify-center py-12">
                  <CircularProgress color="secondary" />
                </div>
              ) : (
                <TrainerMemberLogTable
                  logs={trainerLogs}
                  mode="trainer"
                  loading={trainerLogsLoading}
                />
              )}
            </Box>
          )}
        </Paper>
      </div>
    
    </>
  );
}

export default MemberTrainerLogReport;
